import ResetIcon from "@material-design-icons/svg/outlined/refresh.svg?react";
import {
  Button,
  cn,
  Dropdown,
  DropdownContent,
  DropdownItemPlain,
  DropdownPortal,
  DropdownTrigger,
  FilterSettingsButton,
  Slider,
} from "@jana-technology/platform-ui";

import type { RiskAssetFiltersProps } from "./types";
import RiskAssetFilterItem from "./RiskAssetFilterItem";
import RiskAssetFilterItemGrid from "./RiskAssetFilterItemGrid";
import RiskAssetFilterItemSelect from "./RiskAssetFilterItemSelect";
import {
  initSelectedRiskAssetFiltersState,
  keepStrings,
} from "contexts/filters";
import { isEqual } from "lodash-es";

// consider extracting this to Platform UI post-launch
export const RiskAssetFilters = ({
  open,
  onOpenChange,
  activeFilterCount,
  onApplyButtonClick,
  onFilterButtonClick,
  onResetButtonClick,
  baseFilters,
  selectedRiskAssetFilters,
  savedRiskAssetFilters,
  setSelectedRiskAssetFilters,
  setSavedRiskAssetFilters,
}: RiskAssetFiltersProps) => {
  const {
    wellAgeRange,
    mostRecentCorrosionLogs,
    riskQuartiles,
    wellConfigurations,
    wellTypes,
  } = selectedRiskAssetFilters;

  return (
    <Dropdown modal open={open} onOpenChange={onOpenChange}>
      <DropdownTrigger>
        <FilterSettingsButton
          className={cn("rounded p-2", {
            "bg-grey-300": open,
            "fill-black hover:fill-grey-900": open,
          })}
          badgeCount={activeFilterCount}
          onClick={onFilterButtonClick}
        />
      </DropdownTrigger>
      <DropdownPortal>
        <DropdownContent
          side="right"
          align="start"
          sideOffset={40}
          className="flex w-80 flex-col gap-4 p-4 shadow-toast [&>*]:outline-none [&>*]:focus:outline-none"
        >
          <DropdownItemPlain
            asChild
            onSelect={(event) => {
              event.preventDefault();
            }}
          >
            <header className="flex items-baseline justify-between">
              <h3 className="mr-2 font-semibold">Filters</h3>
              <button
                className="text-grey-700"
                onClick={(event) => {
                  setSelectedRiskAssetFilters(
                    initSelectedRiskAssetFiltersState(baseFilters),
                  );

                  onResetButtonClick?.(event);
                }}
              >
                <ResetIcon className="mr-1 inline h-4 w-4 rotate-180 -scale-y-100 fill-current" />
                Reset All
              </button>
            </header>
          </DropdownItemPlain>
          {baseFilters?.wellAgeRange && (
            <RiskAssetFilterItem heading="Well Age (years)">
              <Slider
                compact
                showInput
                value={wellAgeRange}
                onValueChange={(value) =>
                  setSelectedRiskAssetFilters((prev) => ({
                    ...prev,
                    wellAgeRange: value,
                  }))
                }
                min={baseFilters.wellAgeRange[0]}
                max={baseFilters.wellAgeRange[1]}
                step={1}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters?.wellConfigurations && (
            <RiskAssetFilterItem heading="Well Configuration">
              <RiskAssetFilterItemGrid
                stateKey="wellConfigurations"
                stateItems={wellConfigurations}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.wellConfigurations
                  .filter(keepStrings)
                  .map((config) => ({
                    children: `Config-${config}`,
                    value: config,
                  }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters?.mostRecentCorrosionLogs && (
            <RiskAssetFilterItem heading="Most Recent Intervention">
              <RiskAssetFilterItemGrid
                stateKey="mostRecentCorrosionLogs"
                stateItems={mostRecentCorrosionLogs}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.mostRecentCorrosionLogs
                  .filter(keepStrings)
                  .map((log) => ({
                    children: log,
                    value: log,
                  }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters.wellTypes && (
            <RiskAssetFilterItem heading="Well Type">
              <RiskAssetFilterItemSelect
                id="well-type"
                label="Well Type"
                stateKey="wellTypes"
                stateItems={wellTypes}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.wellTypes.filter(Boolean).map((type) => ({
                  children: type,
                  value: type,
                }))}
              />
            </RiskAssetFilterItem>
          )}
          {baseFilters.riskQuartiles && (
            <RiskAssetFilterItem heading="Risk (Quartile)">
              <RiskAssetFilterItemSelect
                id="risk-quartile"
                label="Risk (Quartile)"
                stateKey="riskQuartiles"
                stateItems={riskQuartiles}
                setState={setSelectedRiskAssetFilters}
                baseFilters={baseFilters}
                items={baseFilters.riskQuartiles.map((quartile) => ({
                  children: quartile.value,
                  value: String(quartile.id),
                }))}
              />
            </RiskAssetFilterItem>
          )}
          <DropdownItemPlain asChild>
            <Button
              onClick={(event) => {
                const hasFilterChanged = isEqual(
                  selectedRiskAssetFilters,
                  savedRiskAssetFilters,
                );
                setSavedRiskAssetFilters(selectedRiskAssetFilters);

                onApplyButtonClick?.(event, hasFilterChanged);
              }}
            >
              Apply Filters
            </Button>
          </DropdownItemPlain>
        </DropdownContent>
      </DropdownPortal>
    </Dropdown>
  );
};

export default RiskAssetFilters;
